import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useEffectEvent,
} from 'react';
import { Check, AlertCircle, Search } from 'lucide-react';

import { Button } from '../ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';

import { useInvestorImportMatchFunds } from '@/hooks/react-query/useInvestorImport';
import { useFunds } from '@/hooks/react-query/useFunds';
import type { FundMatchingData } from '../../types/investor-import';
import type { FundSummary } from '@/types/fund';

interface UnmatchedFundSelectorProps {
  importFundName: string;
  availableFunds: FundSummary[];
  onSelect: (importFundName: string, fundId: string) => void;
}

const UnmatchedFundSelector = ({
  importFundName,
  availableFunds,
  onSelect,
}: UnmatchedFundSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredFunds = availableFunds.filter(
    (fund) =>
      fund.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fund.display_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-3">
      <p className="text-sm text-amber-600">
        No matching fund found. Select a fund or leave unmapped to skip these
        investors.
      </p>

      {/* Fund Search */}
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search funds..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Fund Selection */}
      <div className="max-h-40 overflow-y-auto space-y-1">
        {filteredFunds.map((fund) => (
          <button
            key={fund.id}
            onClick={() => onSelect(importFundName, fund.id)}
            className="w-full text-left p-2 rounded hover:bg-gray-100 border border-gray-200"
          >
            <div className="font-medium text-sm">{fund.name}</div>
            {fund.display_name && fund.display_name !== fund.name && (
              <div className="text-xs text-gray-600">{fund.display_name}</div>
            )}
            <div className="text-xs text-gray-500">
              {fund.fund_type} • {fund.base_currency}
            </div>
          </button>
        ))}
        {filteredFunds.length === 0 && (
          <p className="text-sm text-gray-500 p-2">
            No funds found matching &quot;{searchTerm}&quot;
          </p>
        )}
      </div>
    </div>
  );
};

interface MatchStatusSummaryProps {
  matchedCount: number;
  unmatchedCount: number;
}

const MatchStatusSummary = ({
  matchedCount,
  unmatchedCount,
}: MatchStatusSummaryProps) => {
  if (matchedCount === 0) {
    return (
      <span className="text-red-600">
        At least one fund must be matched to continue
      </span>
    );
  }

  if (unmatchedCount > 0) {
    return (
      <span className="text-amber-600">
        {matchedCount} fund{matchedCount !== 1 ? 's' : ''} matched •{' '}
        {unmatchedCount} will be skipped
      </span>
    );
  }

  return (
    <span className="text-green-600 font-medium">
      ✓ All {matchedCount} funds matched successfully
    </span>
  );
};

interface FundMatchingStepProps {
  sessionId: string;
  onFundsMatched: (matches: FundMatchingData[]) => void;
}

export const FundMatchingStep = ({
  sessionId,
  onFundsMatched,
}: FundMatchingStepProps) => {
  const [matchOverrides, setMatchOverrides] = useState<
    Record<string, Partial<FundMatchingData>>
  >({});

  const matchFundsMutation = useInvestorImportMatchFunds();
  const { data: fundsData } = useFunds();

  const availableFunds = useMemo(() => fundsData || [], [fundsData]);

  // Load suggested fund matches - only once when component mounts
  const loadMatches = useEffectEvent(() => {
    matchFundsMutation.mutate(sessionId);
  });

  useEffect(() => {
    if (sessionId) {
      loadMatches();
    }
  }, [sessionId]);

  const fundMatches = useMemo<FundMatchingData[]>(() => {
    const data = matchFundsMutation.data ?? [];
    return data.map((match) => {
      const override = matchOverrides[match.import_fund_name];
      return override ? { ...match, ...override } : match;
    });
  }, [matchFundsMutation.data, matchOverrides]);

  const handleFundSelection = useCallback(
    (importFundName: string, selectedFundId: string) => {
      const selectedFund = availableFunds.find((f) => f.id === selectedFundId);
      setMatchOverrides((prev) => ({
        ...prev,
        [importFundName]: {
          suggested_fund_id: selectedFundId,
          suggested_fund_name: selectedFund?.name || '',
          is_matched: true,
          confidence_score: 1.0, // Manual selection = 100% confidence
        },
      }));
    },
    [availableFunds]
  );

  const handleRemoveMatch = useCallback((importFundName: string) => {
    setMatchOverrides((prev) => ({
      ...prev,
      [importFundName]: {
        suggested_fund_id: undefined,
        suggested_fund_name: undefined,
        is_matched: false,
        confidence_score: 0,
      },
    }));
  }, []);

  // Notify parent when fund matches change
  useEffect(() => {
    if (fundMatches.length > 0) {
      onFundsMatched(fundMatches);
    }
  }, [fundMatches, onFundsMatched]);

  const matchedCount = fundMatches.filter((match) => match.is_matched).length;
  const unmatchedCount = fundMatches.length - matchedCount;
  const hasAtLeastOneMatch = matchedCount > 0;

  return (
    <div className="space-y-6">
      {/* Progress Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Fund Matching Progress</span>
            <Badge variant={hasAtLeastOneMatch ? 'default' : 'outline'}>
              {matchedCount} of {fundMatches.length} matched
            </Badge>
          </CardTitle>
          <CardDescription>
            Map fund names from the import file to existing funds in the
            database.
            <strong> No new funds will be created.</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Important: Fund Matching</p>
                <p>
                  At least one fund must be matched to continue. Investors
                  associated with unmatched funds will be skipped during import.
                  This allows you to selectively import investors.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fund Matches */}
      <Card>
        <CardHeader>
          <CardTitle>Fund Matches</CardTitle>
          <CardDescription>
            Review and adjust the suggested fund matches below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {fundMatches.map((match) => (
            <div
              key={match.import_fund_name}
              className={`border rounded-lg p-4 ${
                match.is_matched
                  ? 'border-green-200 bg-green-50'
                  : 'border-amber-200 bg-amber-50'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-medium text-gray-900">
                      {match.import_fund_name}
                    </h4>
                    {match.is_matched ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                    )}
                    {!match.is_matched && (
                      <Badge
                        variant="outline"
                        className="text-xs bg-amber-100 text-amber-800 border-amber-300"
                      >
                        Will Skip
                      </Badge>
                    )}
                  </div>

                  {match.is_matched && match.suggested_fund_name ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">
                          Matched to:{' '}
                          <span className="font-medium">
                            {match.suggested_fund_name}
                          </span>
                        </p>
                        {match.confidence_score < 1.0 && (
                          <p className="text-xs text-gray-500">
                            Confidence:{' '}
                            {Math.round(match.confidence_score * 100)}%
                          </p>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          handleRemoveMatch(match.import_fund_name)
                        }
                      >
                        Change
                      </Button>
                    </div>
                  ) : (
                    <UnmatchedFundSelector
                      importFundName={match.import_fund_name}
                      availableFunds={availableFunds}
                      onSelect={handleFundSelection}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}

          {fundMatches.length === 0 && !matchFundsMutation.isPending && (
            <div className="text-center py-8 text-gray-500">
              <p>No fund matching data available.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status Summary */}
      <div className="text-sm text-gray-600">
        <MatchStatusSummary
          matchedCount={matchedCount}
          unmatchedCount={unmatchedCount}
        />
      </div>
    </div>
  );
};
